import { useCallback, useState } from 'react';
import { api } from '@/lib/api-client';
import type { DraftFormState, DraftIndividual, DraftResponse } from './types';
import { emptyFormState } from './types';

type ValidationErrors = { field: string; message: string }[];

export function useClientDraft() {
  const [clientId, setClientId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const saveDraft = useCallback(
    async (form: DraftFormState): Promise<string> => {
      setIsSaving(true);
      try {
        // The CBS form carries pricing for rehydration only — the webapp's
        // CreateClientDraftDto runs ValidationPipe with `forbidNonWhitelisted`
        // and would 400 on the unknown key. Drop it before serialising.
        // Pricing is owned server-side and seeded with defaults at draft
        // creation; the operator edits it later via the Pricing tab.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { pricing: _pricing, ...rest } = form;
        // Empty strings would be rejected by the webapp's class-validator
        // ISO-2 country code constraints (`@IsOptional` only short-circuits
        // on null/undefined). Strip them recursively so optional fields
        // become true `undefined` on the wire.
        const body = stripEmptyStrings(rest) as unknown as Record<string, unknown>;
        if (clientId) {
          await api.put<DraftResponse>(`/api/clients/${clientId}/draft`, body);
          return clientId;
        }
        const data = await api.post<DraftResponse>('/api/clients/draft', body);
        setClientId(data.client_id);
        return data.client_id;
      } finally {
        setIsSaving(false);
      }
    },
    [clientId],
  );

  const submit = useCallback(
    async (form: DraftFormState): Promise<DraftResponse> => {
      setIsSubmitting(true);
      setFieldErrors({});
      try {
        // Save first so any in-flight edits land in the DB before
        // the backend runs the completeness check. saveDraft
        // returns the id so we don't rely on the stale closure.
        const savedId = await saveDraft(form);
        return await api.post<DraftResponse>(`/api/clients/${savedId}/submit`, {});
      } catch (err: unknown) {
        const body = (err as { body?: unknown }).body as { errors?: ValidationErrors } | undefined;
        if (body?.errors) {
          const mapped: Record<string, string> = {};
          for (const e of body.errors) {
            mapped[e.field] = e.message;
          }
          setFieldErrors(mapped);
        }
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [saveDraft],
  );

  const deleteDraft = useCallback(async (): Promise<void> => {
    if (!clientId) return;
    await api.delete<void>(`/api/clients/${clientId}/draft`);
    setClientId(null);
  }, [clientId]);

  const loadDraft = useCallback(async (draftId: number): Promise<DraftFormState> => {
    const data = await api.get<Record<string, unknown>>(`/api/clients/${draftId}/draft`);
    setClientId(String(draftId));
    return mapDraftResponse(data);
  }, []);

  return {
    clientId,
    isSaving,
    isSubmitting,
    fieldErrors,
    saveDraft,
    submit,
    deleteDraft,
    loadDraft,
    setClientId,
  };
}

/**
 * Recursively drop empty-string values from a tree of plain objects and
 * arrays. Numbers, booleans, null, and non-empty strings are preserved.
 * Object children that become empty after their own recursive strip are
 * also dropped so a never-touched address ends up as `undefined` on the
 * wire, not `{}`.
 *
 * The webapp's draft DTOs validate optional ISO-2 country codes with
 * `@Matches(/^[A-Z]{2}$/)` — sending `""` from a cleared dropdown or a
 * never-typed input would surface as a 400 "Validation failed" without
 * this guard.
 */
export function stripEmptyStrings<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => stripEmptyStrings(item)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === '') continue;
      const stripped = stripEmptyStrings(v);
      if (
        stripped !== null &&
        typeof stripped === 'object' &&
        !Array.isArray(stripped) &&
        Object.keys(stripped as Record<string, unknown>).length === 0
      ) {
        continue;
      }
      out[k] = stripped;
    }
    return out as T;
  }
  return value;
}

type SnakeObj = Record<string, unknown> | null | undefined;

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}
function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

function mapAddress(raw: SnakeObj): DraftFormState['companyDetails']['registeredAddress'] {
  if (!raw) return { line1: '', city: '', zip: '', countryCode: '' };
  return {
    line1: str(raw.line1) ?? '',
    line2: str(raw.line2),
    city: str(raw.city) ?? '',
    state: str(raw.state),
    zip: str(raw.zip) ?? '',
    countryCode: str(raw.country_code) ?? '',
  };
}

function mapDraftResponse(data: Record<string, unknown>): DraftFormState {
  const state = emptyFormState();
  const company = data.company_details as SnakeObj;
  if (company) {
    state.companyDetails = {
      companyName: str(company.company_name) ?? '',
      countryCode: str(company.country_code) ?? '',
      legalFormId: num(company.legal_form_id),
      incorporationDate: str(company.incorporation_date),
      incorporationNumber: str(company.incorporation_number),
      businessSectorId: num(company.business_sector_id),
      businessActivityId: num(company.business_activity_id),
      website: str(company.website),
      telephone: str(company.telephone),
      contactEmail: str(company.contact_email),
      tin: str(company.tin),
      registeredAddress: mapAddress(company.registered_address as SnakeObj),
      businessAddress: company.business_address
        ? mapAddress(company.business_address as SnakeObj)
        : undefined,
    };
  }
  const usage = data.account_usage as SnakeObj;
  if (usage) {
    state.accountUsage = {
      estimatedIncomingVolume: num(usage.estimated_incoming_volume),
      estimatedIncomingTxCount: num(usage.estimated_incoming_tx_count),
      estimatedOutgoingVolume: num(usage.estimated_outgoing_volume),
      estimatedOutgoingTxCount: num(usage.estimated_outgoing_tx_count),
      sourceOfIncome: str(usage.source_of_income),
      monthlyIncome: num(usage.monthly_income),
      purposeOfAccount: str(usage.purpose_of_account),
      intendedUse: str(usage.intended_use),
    };
  }
  const pricing = data.pricing as SnakeObj;
  if (pricing) {
    // The draft endpoint emits camelCase keys — reading snake_case here was a
    // pre-existing mismatch that silently dropped the whole block.
    state.pricing = {
      paymentServiceCode: str(pricing.paymentServiceCode),
      outgoingFeeFixedValue: str(pricing.outgoingFeeFixedValue),
      outgoingFeePercentValue: str(pricing.outgoingFeePercentValue),
      incomingFeeFixedValue: str(pricing.incomingFeeFixedValue),
      incomingFeePercentValue: str(pricing.incomingFeePercentValue),
    };
  }
  const individuals = Array.isArray(data.individuals)
    ? (data.individuals as Record<string, unknown>[])
    : [];
  state.individuals = individuals.map(
    (raw): DraftIndividual => ({
      firstName: str(raw.first_name) ?? '',
      lastName: str(raw.last_name) ?? '',
      email: str(raw.email) ?? '',
      roleId: num(raw.role_id) ?? 1,
      gender: (str(raw.gender) as DraftIndividual['gender']) ?? undefined,
      dateOfBirth: str(raw.date_of_birth),
      nationalityCountryCode: str(raw.nationality_country_code),
      residenceCountryCode: str(raw.residence_country_code),
      telephone: str(raw.telephone),
      addressLine1: str(raw.address_line1),
      addressLine2: str(raw.address_line2),
      addressCity: str(raw.address_city),
      addressState: str(raw.address_state),
      addressZip: str(raw.address_zip),
      addressCountryCode: str(raw.address_country_code),
      ownershipPercent: num(raw.ownership_percent),
      isPep: bool(raw.is_pep, false),
      pepName: str(raw.pep_name),
      pepRelationship: str(raw.pep_relationship),
      usResident: bool(raw.us_resident, false),
      usTin: str(raw.us_tin),
      residenceTin: str(raw.residence_tin),
      canLogin: bool(raw.can_login, false),
    }),
  );
  return state;
}
